import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { InputProps as StandardInputProps } from '@mui/material/Input/Input';
import {
  Fade,
  IconButton,
  Switch,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import BorderedSection from '../../shared/borderedSection';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { FormValues } from '../types/userDetailFormValues';
import { AuthorizedUser } from '../../../auth/models';
import useCopyToClipboard from '../../../ui/helpers/copy-to-clipboard';
import { Notifier } from '../../../system/services/notificationManager';
/* ICONS */
import CopyIcon from '@mui/icons-material/CopyAllRounded';
import SendIcon from '@mui/icons-material/Send';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  useInitatePasswordResetManagerMutation,
  useUnlockUserMutation,
  User,
} from '../../../../types/generated-types';
import { useApolloClient } from '@apollo/client';

export function UserGeneralTab(props: {
  currentUser: AuthorizedUser;
  formik: ReturnType<typeof useFormik<FormValues>>;
  apiToken: string | null | undefined;
  user: Partial<User>;
  onChange: StandardInputProps['onChange'];
}) {
  const [clipboardValue, copyToClipboard] = useCopyToClipboard();
  const [initiatePasswordReset] = useInitatePasswordResetManagerMutation();
  const [unlockUser] = useUnlockUserMutation();
  const client = useApolloClient();
  const [showUnlockConfirmation, setShowUnlockConfirmation] = useState(false);

  return (
    <>
      <Fade in={true} style={{ transitionDuration: '1100ms' }}>
        <div style={{ width: '100%' }}>
          <TextField
            fullWidth
            autoFocus
            id="name"
            name="name"
            placeholder="User Name"
            label="Full Name"
            value={props.formik.values.name}
            onChange={props.onChange}
            error={
              props.formik.touched.name && Boolean(props.formik.errors.name)
            }
            helperText={props.formik.touched.name && props.formik.errors.name}
          />
          <br />
          <br />
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email"
            value={props.formik.values.email}
            onChange={props.onChange}
            error={
              props.formik.touched.email && Boolean(props.formik.errors.email)
            }
            helperText={props.formik.touched.email && props.formik.errors.email}
          />
          <br />
          <br />
          <TextField
            id="status"
            name="status"
            fullWidth
            select
            label="Status"
            defaultValue="new"
            value={props.formik.values.status}
            onChange={props.onChange}
            helperText={
              props.formik.touched.status && props.formik.errors.status
            }
          >
            <MenuItem key="new" value="new">
              New
            </MenuItem>
            <MenuItem key="active" value="active">
              Active
            </MenuItem>
            <MenuItem key="inactive" value="inactive">
              Inactive
            </MenuItem>
          </TextField>
          <br />
          <br />
          <BorderedSection
            title="Role Assignment"
            error={
              (props.formik.errors.roleSelected &&
                (props.formik.touched.isManager ||
                  props.formik.touched.isInstaller ||
                  props.formik.touched.isResident ||
                  props.formik.touched.isSuper)) ||
              false
            }
            hint={props.formik.errors.roleSelected}
          >
            <FormGroup style={{ paddingLeft: '6px' }}>
              <FormControlLabel
                disabled={!props.currentUser.isSuper}
                control={
                  <Switch
                    size="small"
                    onChange={props.onChange}
                    name="isSuper"
                    id="isSuper"
                    checked={props.formik.values.isSuper}
                  />
                }
                label="Super User"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    name="isManager"
                    id="isManager"
                    onChange={props.onChange}
                    checked={props.formik.values.isManager}
                  />
                }
                label="Manager"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    name="isInstaller"
                    id="isInstaller"
                    onChange={props.onChange}
                    checked={props.formik.values.isInstaller}
                  />
                }
                label="Installer"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    name="isResident"
                    id="isResident"
                    onChange={props.onChange}
                    checked={props.formik.values.isResident}
                  />
                }
                label="Resident"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    name="isViewer"
                    id="isViewer"
                    onChange={props.onChange}
                    checked={props.formik.values.isViewer}
                  />
                }
                label="Viewer"
              />
            </FormGroup>
          </BorderedSection>
          {props.currentUser.isSuper && props.apiToken && (
            <BorderedSection style={{ marginTop: '24px' }} title="API Access">
              <div
                style={{ display: 'flex', flexDirection: 'row', width: '100%' }}
              >
                <div
                  style={{
                    lineBreak: 'anywhere',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                  }}
                >
                  {props.apiToken}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    flex: '0 1 auto',
                    borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
                    marginLeft: '10px',
                  }}
                >
                  <IconButton
                    onClick={async () => {
                      if (props.apiToken) {
                        await copyToClipboard(props.apiToken);
                        console.log('Copied to clipboard', clipboardValue);
                        Notifier.success('API Token copied to clipboard');
                      } else {
                        Notifier.error(
                          'No API Token available to copy to clipboard',
                        );
                      }
                    }}
                  >
                    <CopyIcon />
                  </IconButton>
                </div>
              </div>
            </BorderedSection>
          )}
          {props.currentUser.isSuper && (
            <BorderedSection
              title="User Account Actions"
              style={{ marginTop: '24px' }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'wrap',
                  padding: '2px',
                }}
              >
                <Button
                  sx={{ color: '#fff' }}
                  variant="contained"
                  color="secondary"
                  type="submit"
                  endIcon={<SendIcon />}
                  onClick={() => {
                    initiatePasswordReset({
                      variables: {
                        userId: props.formik.values.id,
                      },
                    })
                      .then((res) => {
                        Notifier.success(
                          'Password reset message sent to user.',
                        );
                      })
                      .catch((error) => {
                        Notifier.error(
                          `Error sending password reset: ${error.message}.`,
                        );
                      });
                  }}
                >
                  Send Password Reset
                </Button>
                {/* Show unlock button only if user is locked and current user is super */}
                {props.user.editableAuthenticationProfile?.lockedAt &&
                props.user.editableAuthenticationProfile.lockedAt > 0 ? (
                  <Button
                    sx={{ color: '#000' }}
                    variant="contained"
                    color="warning"
                    endIcon={<LockOpenIcon />}
                    onClick={() => setShowUnlockConfirmation(true)}
                  >
                    Unlock User
                  </Button>
                ) : null}
              </div>
            </BorderedSection>
          )}
        </div>
      </Fade>
      <Dialog
        open={showUnlockConfirmation}
        onClose={() => setShowUnlockConfirmation(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Typography variant="h4" sx={{ padding: '15px' }}>
          Unlock User Account
        </Typography>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to unlock this user account? This will allow
            them to log in again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnlockConfirmation(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            sx={{ color: '#fff' }}
            onClick={async () => {
              try {
                await unlockUser({
                  variables: {
                    userId: props.formik.values.id,
                  },
                });
                Notifier.success('User account unlocked successfully');
                // Invalidate the user detail query to refresh the data
                client.refetchQueries({
                  include: ['UserDetail'],
                });
                setShowUnlockConfirmation(false);
              } catch (error) {
                Notifier.error(
                  `Error unlocking user account: ${
                    error instanceof Error ? error.message : 'Unknown error'
                  }.`,
                );
              }
            }}
            autoFocus
          >
            Unlock
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
